import React, { useMemo, useState } from 'react'
import { api, formatCurrency } from '../../utils/api'
import { theme } from './warehouseTheme'
import ProductTypeSelector, { masterProductTypes } from './ProductTypeSelector'
import { parsePounds } from './splitItem'

/**
 * Extra items at Receiving (Trac #851): things found in the boxes that are not
 * on the deal. Each one is added as a real quote line, already received, with
 * the note "added at Receiving". The buy price is what the customer is paid
 * for it and defaults to nothing, so their total does not change.
 */

const MAX_NAME_LENGTH = 200
const EMPTY_FORM = { name: '', mainCategory: '', subCategory: '', quantity: '1', buyPrice: '0.00', sellPrice: '', note: '' }

/** What is wrong with the form, as one phrase per problem. Empty means it can be sent. */
export function extraFormProblems(form) {
  const problems = []
  const name = form.name.trim()
  if (!name) problems.push('Say what it is')
  else if (name.length > MAX_NAME_LENGTH) problems.push(`The name can be up to ${MAX_NAME_LENGTH} characters`)
  if (!form.mainCategory || !form.subCategory) problems.push('Choose a category')
  const quantity = Number(form.quantity)
  if (String(form.quantity).trim() === '' || !Number.isInteger(quantity) || quantity < 1) problems.push('How many must be a whole number, 1 or more')
  if (parsePounds(form.buyPrice) === null) problems.push('The buy price is not a valid amount')
  if (parsePounds(form.sellPrice) === null) problems.push('The sell price is not a valid amount')
  return problems
}

/** The request body. Prices go as pounds; the worker turns them into pence. */
export function extraRequestBody(quoteId, form) {
  return {
    quoteId,
    name: form.name.trim(),
    mainCategory: form.mainCategory,
    subCategory: form.subCategory,
    quantity: Number(form.quantity),
    buyPrice: parsePounds(form.buyPrice) / 100,
    sellPrice: parsePounds(form.sellPrice) / 100,
    note: form.note.trim()
  }
}

const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: theme.colors.grey500, marginBottom: '6px' }
const fieldStyle = {
  width: '100%', padding: '12px 14px', border: `1px solid ${theme.colors.grey300}`, borderRadius: theme.radius.md,
  fontSize: '14px', background: theme.colors.grey100, outline: 'none', fontFamily: 'inherit', textAlign: 'left'
}

function Field({ label, children, style }) {
  return (
    <label style={{ display: 'block', ...style }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </label>
  )
}

function ExtraRow({ extra, onRemove, busy }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '14px', padding: '14px', marginBottom: '8px',
      background: theme.colors.amberLight, border: `2px solid ${theme.colors.sunburst}`, borderRadius: theme.radius.md
    }}>
      <div style={{
        width: '24px', height: '24px', borderRadius: '50%', background: theme.colors.sunburst, color: theme.colors.charcoal,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0
      }}>+</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{extra.name}</div>
        <div style={{ fontSize: '13px', color: theme.colors.grey500, marginTop: '2px' }}>
          {extra.subCategory} · {extra.quantity} unit{extra.quantity === 1 ? '' : 's'} · Buy: {formatCurrency(extra.buyPrice)} · Sell: {formatCurrency(extra.sellPrice)} · added at Receiving
        </div>
      </div>
      <button type="button" onClick={() => onRemove(extra)} disabled={busy}
        style={{ background: 'transparent', border: 'none', color: theme.colors.grey500, cursor: busy ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
        Remove
      </button>
    </div>
  )
}

function ExtraForm({ quoteId, onAdded, onCancel }) {
  const [form, setForm] = useState(EMPTY_FORM)
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [tried, setTried] = useState(false)
  const groups = useMemo(() => masterProductTypes(), [])
  const problems = extraFormProblems(form)

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }))

  const submit = async () => {
    setTried(true)
    if (problems.length > 0) return
    setBusy(true)
    setError(null)
    try {
      const response = await api.post('/api/warehouse/receiving/extra', extraRequestBody(quoteId, form))
      onAdded({ ...response.item, subCategory: form.subCategory, quantity: Number(form.quantity), buyPrice: parsePounds(form.buyPrice) / 100, sellPrice: parsePounds(form.sellPrice) / 100 })
    } catch (err) {
      setError(err.message || 'The item could not be added')
    } finally {
      setBusy(false)
    }
  }

  if (picking) {
    return (
      <ProductTypeSelector
        productTypes={groups}
        currentSelection={form.subCategory}
        onSelect={(sub, main) => { set({ subCategory: sub, mainCategory: main }); setPicking(false) }}
        onCancel={() => setPicking(false)}
      />
    )
  }

  return (
    <div style={{ background: theme.colors.grey100, border: `1px solid ${theme.colors.grey200}`, borderRadius: theme.radius.lg, padding: '20px' }}>
      <div style={{ fontWeight: 700, marginBottom: '14px' }}>Add an item that's not on this deal</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.2fr) minmax(0, 0.6fr)', gap: '12px', marginBottom: '12px' }}>
        <Field label="What is it?">
          <input style={fieldStyle} value={form.name} onChange={e => set({ name: e.target.value })} placeholder="QED speaker cable 2 × 3m" />
        </Field>
        <Field label="Category">
          <button type="button" aria-label="Category" style={{ ...fieldStyle, cursor: 'pointer', color: form.subCategory ? theme.colors.charcoal : theme.colors.grey500 }}
            onClick={() => setPicking(true)}>
            {form.subCategory || 'Choose category'}
          </button>
        </Field>
        <Field label="How many">
          <input style={fieldStyle} inputMode="numeric" value={form.quantity} onChange={e => set({ quantity: e.target.value })} />
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px', marginBottom: '6px' }}>
        <Field label="Buy price (paid to customer)">
          <input style={fieldStyle} inputMode="decimal" value={form.buyPrice} onChange={e => set({ buyPrice: e.target.value })} />
        </Field>
        <Field label="Sell price">
          <input style={fieldStyle} inputMode="decimal" value={form.sellPrice} onChange={e => set({ sellPrice: e.target.value })} placeholder="0.00" />
        </Field>
      </div>
      <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '12px' }}>
        Leave buy at £0 and the customer's total doesn't change.
      </div>
      <Field label="Note (optional)" style={{ marginBottom: '14px' }}>
        <input style={fieldStyle} value={form.note} onChange={e => set({ note: e.target.value })} placeholder="Found loose in box 3" />
      </Field>

      {tried && problems.length > 0 && (
        <div role="alert" style={{ color: theme.colors.crimson, fontSize: '13px', marginBottom: '12px' }}>
          {problems.map(problem => <div key={problem}>{problem}</div>)}
        </div>
      )}
      {error && (
        <div role="alert" style={{ background: theme.colors.redLight, border: `1px solid ${theme.colors.crimson}`, borderRadius: theme.radius.md, padding: '10px 14px', fontSize: '14px', marginBottom: '12px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button type="button" onClick={onCancel} disabled={busy}
          style={{ padding: '12px 20px', border: `1px solid ${theme.colors.grey300}`, borderRadius: theme.radius.md, background: theme.colors.white, fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={busy}
          style={{ padding: '12px 20px', border: 'none', borderRadius: theme.radius.md, background: theme.colors.sunburst, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1 }}>
          {busy ? 'Adding…' : 'Add extra'}
        </button>
      </div>
    </div>
  )
}

/** The extras list and the add form, for the Receiving confirm step. */
export default function ReceivingExtras({ quoteId, extras, onAdded, onRemoved }) {
  const [open, setOpen] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState(null)

  // Take back an extra added by mistake. The worker rejects the line at once.
  const remove = async (extra) => {
    if (!window.confirm(`Remove "${extra.name}" from this deal?`)) return
    setRemoving(true)
    setRemoveError(null)
    try {
      await api.post(`/api/warehouse/receiving/extra/${encodeURIComponent(extra.id)}/remove`, { quoteId })
      onRemoved(extra.id)
    } catch (err) {
      setRemoveError(err.message || 'The item could not be removed')
    } finally {
      setRemoving(false)
    }
  }

  return (
    <div style={{ padding: '0 12px 12px' }}>
      {extras.length > 0 && (
        <>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: theme.colors.grey500, margin: '8px 4px 10px' }}>
            Extra items in the boxes ({extras.length})
          </div>
          {extras.map(extra => <ExtraRow key={extra.id} extra={extra} onRemove={remove} busy={removing} />)}
          {removeError && (
            <div role="alert" style={{ color: theme.colors.crimson, fontSize: '13px', margin: '0 4px 10px' }}>{removeError}</div>
          )}
        </>
      )}
      {open ? (
        <ExtraForm quoteId={quoteId} onCancel={() => setOpen(false)} onAdded={(extra) => { onAdded(extra); setOpen(false) }} />
      ) : (
        <button type="button" onClick={() => setOpen(true)}
          style={{ padding: '10px 16px', border: `1px dashed ${theme.colors.grey400}`, borderRadius: theme.radius.md, background: theme.colors.white, fontWeight: 600, cursor: 'pointer' }}>
          + Add an item that's not on this deal
        </button>
      )}
    </div>
  )
}
